<template>
  <BlockFieldset class="project-financial-model">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <legend>
        <strong>{{ $t('project_financial_model') }}</strong>
      </legend>
      <ButtonWait
        v-if="!isBlockReadOnly"
        :startCallback="startFinancialModelBuild"
        :pollCallback="pollFinancialModelBuildState"
        class="mr-2"
      >
        <template v-slot:buttonText>
          {{ $t('generateFinancialModel') }}
        </template>
      </ButtonWait>
      <div v-if="project.attachments.length > 0" class="export-files-section mt-2">
        <AttachmentList
          :attachments="project.attachments"
          :attachmentType="financialModelAttachmentType"
          :inline="true"
          :deletable="!isBlockReadOnly"
          :hidesDeleteForGuests="!isOrganizationRoleNavigationEnabled"
          v-on:attachment-list:delete="onDeleteAttachment"
        />
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AttachmentList from '@/components/AttachmentList';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import ButtonWait from '@/components/ButtonWait.vue';
import { buildStateOfType } from '@/helpers/attachmentBuildState';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { PROJECT_FINANCIAL_MODEL_FIELDS } from './projectFinancialModelFragment';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/** Matches PROJECT_ATTACHMENT_TYPES.FIN_MODEL on the server. */
const FIN_MODEL_ATTACHMENT_TYPE = 'FIN_MODEL';

/** Field of the block on `ProjectBlocks`. */
const FINANCIAL_MODEL_BLOCK_FIELD = 'financialModel';

/**
 * Block project-financial-model (plan docs/work/_done/2026-10-project-block-financial-model): generate
 * the Finanzmodell (XLSX) and list the generated files. `project` is the block data
 * (`Project.blocks.financialModel`, only FIN_MODEL attachments); every operation reloads it
 * through `fetchProjectBlockByNumber`. No legacy role rules: only the level counts, and with
 * the switch off AttachmentList keeps hiding delete for guests.
 */
export default {
  name: 'ProjectFinancialModel',
  components: {
    AttachmentList,
    BlockFieldset,
    ButtonWait,
  },
  mixins: [AccessBlockMixin],
  props: {
    project: { type: Object, required: true },
  },
  data() {
    return {
      financialModelAttachmentType: FIN_MODEL_ATTACHMENT_TYPE,
    };
  },
  computed: {
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
  },
  methods: {
    ...mapActions(['buildFinModelExport', 'fetchProjectBlockByNumber', 'deleteProjectAttachment']),
    reloadBlock() {
      return this.fetchProjectBlockByNumber({
        projectNumber: this.project.number,
        blockField: FINANCIAL_MODEL_BLOCK_FIELD,
        fragment: PROJECT_FINANCIAL_MODEL_FIELDS,
      });
    },
    async startFinancialModelBuild() {
      await this.buildFinModelExport({ projectNumber: this.project.number });
    },
    /** Build state of FIN_MODEL from the reloaded block, for ButtonWait. */
    async pollFinancialModelBuildState() {
      return buildStateOfType(await this.reloadBlock(), FIN_MODEL_ATTACHMENT_TYPE);
    },
    async onDeleteAttachment(event) {
      await this.deleteProjectAttachment({
        projectId: this.project.id,
        attachmentId: event.attachmentId,
        attachmentType: event.attachmentType,
      });
      await this.reloadBlock();
    },
  },
};
</script>
